import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0, limit: 10 });
  const [filters, setFilters] = useState({ search: '', status: '', sortBy: 'createdAt', sortOrder: 'desc' });
  const [formData, setFormData] = useState({
    date: new Date(),
    description: '',
    amount: '',
    currency: 'USD',
    recipient: ''
  });
  const { hasPermission } = useAuth();
  const { setPageTitle } = usePageContext();

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.limit,
        ...filters
      };
      const response = await axios.get('/api/expense', { params });
      setExpenses(Array.isArray(response.data.expenses) ? response.data.expenses : Array.isArray(response.data) ? response.data : []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error('Error fetching expenses');
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await axios.get('/api/expense-recipient');
      setRecipients(response.data);
    } catch (error) {
      toast.error('Error fetching recipients');
    }
  };

  useEffect(() => {
    setPageTitle('Expense Management');
    fetchExpenses();
    fetchRecipients();
  }, [setPageTitle]);

  useEffect(() => {
    fetchExpenses();
  }, [pagination.current, filters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await axios.post('/api/expense', formData);
      toast.success('Expense submitted successfully');
      setShowModal(false);
      setFormData({
        date: new Date(),
        description: '',
        amount: '',
        currency: 'USD',
        recipient: ''
      });
      setSelectedExpenses([]);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error submitting expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (expenseId) => {
    try {
      await axios.delete(`/api/expense/${expenseId}`);
      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Error deleting expense');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedExpenses.map(id => axios.delete(`/api/expense/${id}`)));
      toast.success(`${selectedExpenses.length} expenses deleted successfully`);
      setSelectedExpenses([]);
      setShowDeleteModal(false);
      fetchExpenses();
    } catch (error) {
      toast.error('Error deleting expenses');
    }
  };

  const handleSubmitForApproval = async (expenseId) => {
    try {
      await axios.put(`/api/expense/${expenseId}/submit`);
      toast.success('Expense submitted for approval');
      fetchExpenses();
    } catch (error) {
      toast.error('Error submitting expense');
    }
  };

  const handleApproval = async (expenseId, status) => {
    try {
      await axios.put(`/api/expense/${expenseId}/approve`, { status });
      toast.success(`Expense ${status} successfully`);
      fetchExpenses();
    } catch (error) {
      toast.error('Error updating expense status');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft': return 'bg-secondary';
      case 'pending': return 'bg-warning';
      case 'approved': return 'bg-success';
      case 'rejected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const recipientOptions = recipients.map(r => ({
    value: r._id,
    label: `${r.name} (${r.type.charAt(0).toUpperCase() + r.type.slice(1)})`
  }));

  const canApprove = hasPermission('expense', 'approve');
  const canCreate = hasPermission('expense', 'create');

  if (!hasPermission('expense', 'read')) {
    return <div className="alert alert-danger">Access denied</div>;
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
            <div className="card-body text-white">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-2">💰 Expense Management</h4>
                  <p className="mb-0 opacity-75">Submit, track, and manage business expenses for approval and payment</p>
                  <small className="opacity-75">Draft → Submit → Approve → Payment Processing</small>
                </div>
                <div className="text-end">
                  <div className="h5 mb-0">{expenses.length} Expenses</div>
                  <small className="opacity-75">Total tracked</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">🔍 Search & Filter</h6>
            {canCreate && (
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                <i className="fas fa-plus me-2"></i>Add Expense
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search descriptions..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All Status</option>
                <option value="draft">Draft ({expenses.filter(e => e.status === 'draft').length})</option>
                <option value="pending">Pending ({expenses.filter(e => e.status === 'pending').length})</option>
                <option value="approved">Approved ({expenses.filter(e => e.status === 'approved').length})</option>
                <option value="rejected">Rejected ({expenses.filter(e => e.status === 'rejected').length})</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Sort By</label>
              <select
                className="form-select"
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-');
                  setFilters({...filters, sortBy, sortOrder});
                }}
              >
                <option value="createdAt-desc">Newest First</option>
                <option value="createdAt-asc">Oldest First</option>
                <option value="amount-desc">Amount High-Low</option>
                <option value="amount-asc">Amount Low-High</option>
                <option value="date-desc">Date Recent</option>
                <option value="date-asc">Date Oldest</option>
              </select>
            </div>
            <div className="col-md-5">
              <label className="form-label">Bulk Actions</label>
              <div className="d-flex gap-2">
                {selectedExpenses.length > 0 ? (
                  <>
                    <span className="badge bg-primary align-self-center">{selectedExpenses.length} selected</span>
                    <button className="btn btn-sm btn-danger" onClick={() => setShowDeleteModal(true)}>
                      <i className="fas fa-trash me-1"></i>Delete Selected
                    </button>
                  </>
                ) : (
                  <small className="text-muted align-self-center">Select expenses to perform bulk actions</small>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-2">
          <div className="card border-secondary">
            <div className="card-body text-center">
              <div className="text-secondary mb-2" style={{fontSize: '1.5rem'}}>📝</div>
              <h6 className="text-secondary">Draft</h6>
              <h4 className="mb-0">{expenses.filter(e => e.status === 'draft').length}</h4>
              <small className="text-muted">Not submitted</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card border-warning">
            <div className="card-body text-center">
              <div className="text-warning mb-2" style={{fontSize: '1.5rem'}}>⏳</div>
              <h6 className="text-warning">Pending</h6>
              <h4 className="mb-0">{expenses.filter(e => e.status === 'pending').length}</h4>
              <small className="text-muted">Awaiting approval</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card border-success">
            <div className="card-body text-center">
              <div className="text-success mb-2" style={{fontSize: '1.5rem'}}>✓</div>
              <h6 className="text-success">Approved</h6>
              <h4 className="mb-0">{expenses.filter(e => e.status === 'approved').length}</h4>
              <small className="text-muted">Ready for payment</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card border-danger">
            <div className="card-body text-center">
              <div className="text-danger mb-2" style={{fontSize: '1.5rem'}}>❌</div>
              <h6 className="text-danger">Rejected</h6>
              <h4 className="mb-0">{expenses.filter(e => e.status === 'rejected').length}</h4>
              <small className="text-muted">Not approved</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-info">
            <div className="card-body">
              <div className="row">
                <div className="col-6 text-center">
                  <div className="text-info mb-2" style={{fontSize: '1.5rem'}}>💰</div>
                  <h6 className="text-info">Approved Amount</h6>
                  <h4 className="mb-0">
                    ${expenses
                      .filter(e => e.status === 'approved')
                      .reduce((sum, e) => sum + e.amount, 0)
                      .toFixed(2)}
                  </h4>
                  <small className="text-muted">Released from profit</small>
                </div>
                <div className="col-6 text-center">
                  <div className="text-primary mb-2" style={{fontSize: '1.5rem'}}>📈</div>
                  <h6 className="text-primary">Total Submitted</h6>
                  <h4 className="mb-0">
                    ${expenses
                      .filter(e => e.status !== 'draft')
                      .reduce((sum, e) => sum + e.amount, 0)
                      .toFixed(2)}
                  </h4>
                  <small className="text-muted">All submitted expenses</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading expenses...</p>
        </div>
      ) : expenses.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>💰</div>
            <h5 className="text-muted mt-3">No expenses found</h5>
            <p className="text-muted mb-4">Start tracking your business expenses here.</p>
            <div className="alert alert-info text-start">
              <h6><i className="fas fa-info-circle me-2"></i>Expense Workflow:</h6>
              <ol className="mb-0">
                <li><strong>Create:</strong> Add expense details and save as draft</li>
                <li><strong>Submit:</strong> Submit draft expenses for approval</li>
                <li><strong>Approve:</strong> Admin approves/rejects submitted expenses</li>
                <li><strong>Payment:</strong> Approved expenses become payable to recipients</li>
              </ol>
            </div>
            {hasPermission('expense', 'create') && (
              <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
                <i className="fas fa-plus me-2"></i>Add First Expense
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Expense List</h6>
              <small className="text-muted">{expenses.length} expenses</small>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{width: '40px'}}>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedExpenses(expenses.map(exp => exp._id));
                        } else {
                          setSelectedExpenses([]);
                        }
                      }}
                      checked={selectedExpenses.length === expenses.length && expenses.length > 0}
                    />
                  </th>
                  <th>Date</th>
                  <th>Description & Recipient</th>
                  <th>Amount</th>
                  <th>Submitted By</th>
                  <th>Status</th>
                  <th style={{width: '200px'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(expense => (
                  <tr key={expense._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedExpenses.includes(expense._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedExpenses([...selectedExpenses, expense._id]);
                          } else {
                            setSelectedExpenses(selectedExpenses.filter(id => id !== expense._id));
                          }
                        }}
                      />
                    </td>
                    <td>
                      <div className="fw-semibold">{new Date(expense.date).toLocaleDateString()}</div>
                      <small className="text-muted">{new Date(expense.createdAt).toLocaleDateString()}</small>
                    </td>
                    <td>
                      <div className="fw-semibold">{expense.description}</div>
                      <div className="d-flex align-items-center mt-1">
                        <div className="avatar-circle me-2" style={{ width: '24px', height: '24px', fontSize: '0.7rem' }}>
                          {expense.recipient?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <small className="text-muted">{expense.recipient?.name}</small>
                          <span className={`badge badge-sm ms-2 ${
                            expense.recipient?.type === 'employee' ? 'bg-primary' :
                            expense.recipient?.type === 'vendor' ? 'bg-success' :
                            expense.recipient?.type === 'contractor' ? 'bg-info' :
                            expense.recipient?.type === 'partner' ? 'bg-warning' : 'bg-secondary'
                          }`}>
                            {expense.recipient?.type}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="fw-bold" style={{fontSize: '1.1rem'}}>{expense.currency} {expense.amount.toFixed(2)}</div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-user-circle text-muted me-2"></i>
                        <span>{expense.spentBy?.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(expense.status)}`}>
                        {expense.status.charAt(0).toUpperCase() + expense.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        {expense.status === 'draft' && (
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleSubmitForApproval(expense._id)}
                          >
                            <i className="fas fa-paper-plane me-1"></i>Submit
                          </button>
                        )}
                        {canApprove && expense.status === 'pending' && (
                          <div className="btn-group" role="group">
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => handleApproval(expense._id, 'approved')}
                            >
                              <i className="fas fa-check me-1"></i>Approve
                            </button>
                            <button 
                              className="btn btn-sm btn-danger"
                              onClick={() => handleApproval(expense._id, 'rejected')}
                            >
                              <i className="fas fa-times me-1"></i>Reject
                            </button>
                          </div>
                        )}
                        {expense.status === 'approved' && (
                          <div className="text-success">
                            <small>
                              <i className="fas fa-check-circle me-1"></i>
                              Approved by {expense.approvedBy?.name}<br />
                              <i className="fas fa-calendar me-1"></i>
                              {new Date(expense.approvalDate).toLocaleDateString()}
                            </small>
                          </div>
                        )}
                        {expense.status === 'rejected' && (
                          <div className="text-danger">
                            <small>
                              <i className="fas fa-times-circle me-1"></i>
                              Rejected by {expense.approvedBy?.name}<br />
                              <i className="fas fa-calendar me-1"></i>
                              {new Date(expense.approvalDate).toLocaleDateString()}
                            </small>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Showing {((pagination.current - 1) * pagination.limit) + 1} to {Math.min(pagination.current * pagination.limit, pagination.total)} of {pagination.total} entries
        </div>
        <nav>
          <ul className="pagination mb-0">
            <li className={`page-item ${pagination.current === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination({...pagination, current: pagination.current - 1})} disabled={pagination.current === 1}>
                Previous
              </button>
            </li>
            {[...Array(pagination.pages)].map((_, i) => (
              <li key={i + 1} className={`page-item ${pagination.current === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPagination({...pagination, current: i + 1})}>
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${pagination.current === pagination.pages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination({...pagination, current: pagination.current + 1})} disabled={pagination.current === pagination.pages}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {/* Add Expense Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Expense</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Recipient</label>
                    <Select
                      options={recipientOptions}
                      value={recipientOptions.find(r => r.value === formData.recipient)}
                      onChange={(option) => setFormData({...formData, recipient: option?.value || ''})}
                      placeholder="Select recipient"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Date</label>
                    <DatePicker
                      selected={formData.date}
                      onChange={(date) => setFormData({...formData, date})}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                      maxDate={new Date()}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                      placeholder="Enter expense description..."
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-8 mb-3">
                      <label className="form-label">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Currency</label>
                      <select
                        className="form-select"
                        value={formData.currency}
                        onChange={(e) => setFormData({...formData, currency: e.target.value})}
                      >
                        <option value="USD">USD</option>
                        <option value="INR">INR</option>
                      </select>
                    </div>
                  </div>
                  <div className="alert alert-info">
                    <div className="d-flex align-items-start">
                      <i className="fas fa-info-circle me-2 mt-1"></i>
                      <div>
                        <strong>Expense Process:</strong>
                        <ul className="mb-0 mt-1">
                          <li>Expense saved as <strong>Draft</strong></li>
                          <li>Submit for approval when ready</li>
                          <li>Admin approves/rejects expense</li>
                          <li>Approved expenses become payable to recipient</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      'Save as Draft'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                  <h5 className="mt-3">Are you sure?</h5>
                  <p className="text-muted">
                    You are about to delete {selectedExpenses.length} expense(s). This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-2"></i>Delete {selectedExpenses.length} Expense(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseManagement;